import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// Register the blueprint font-size utilities so tailwind-merge can tell them
// apart from text color classes (both parse as ambiguous `text-*`); without
// this, `cn("text-content-inverse", "text-eyebrow-md")` drops the color.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // `h-button`/`w-button` too, so a component that sizes itself from the
      // button token can still be overridden by the caller's own height.
      h: [
        {
          h: ["3xl", "button"],
        },
      ],
      w: [
        {
          w: ["3xl", "button"],
        },
      ],
      // The blueprint durations, so a variant's own settle replaces the base.
      duration: [
        {
          duration: ["quick", "medium", "long"],
        },
      ],
      "font-size": [
        {
          text: [
            "d1",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "home-hero-headline",
            "paragraph-lg",
            "paragraph-md",
            "paragraph-sm",
            "spec-label",
            "spec-value",
            "bar-price",
            "form-label",
            "wib-card-name",
            "wib-card-body",
            "highlight-card-title",
            "highlight-card-title-lg",
            "highlight-card-title-xl",
            "configurator-title",
            "careers-section-title",
            "careers-job-location",
            "banner-card-title",
            "banner-card-eyebrow-sm",
            "banner-card-eyebrow-md",
            "nav-label",
            "nav-label-sm",
            "nav-menu-link",
            "nav-cart-count",
            "footer-title",
            "side-cart-title",
            "side-cart-product-title",
            "side-cart-body",
            "side-cart-care-body",
            "assurance",
            "side-cart-assurance",
            "sticky-bar-title",
            "sticky-bar-note",
            "sticky-bar-cta",
            "side-cart-gift-theme",
            "side-cart-gift-field",
            "review-attribution",
            "speech-bubble",
            "mini-subtitle",
            "turntable-hint",
            "film-caption",
            "command-pill",
            "language-phrase",
            "language-name",
            "lockup-bubble",
            "story-year",
            "story-line",
            "serene-poster",
            "serene-chapter",
            "serene-intro",
            "serene-italic",
            "eyebrow-accent",
            "eyebrow-section",
            "eyebrow-xl",
            "eyebrow-lg",
            "eyebrow-md",
            "eyebrow-sm",
            "highlight-card-eyebrow",
            "highlight-card-eyebrow-lg",
            "highlight-card-eyebrow-xl",
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Holds a scroll-progress fraction inside its own range. */
export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Reads `amount` of the way from `from` to `to`. */
export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

/**
 * Executes callback after the browser has painted.
 * Uses double requestAnimationFrame to ensure the browser has
 * completed rendering before the callback runs.
 */
export function afterPaint(callback: () => void): number {
  return requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}
